import { useListState } from "@/hooks/use-list-state";
import { useState, useEffect, useMemo } from "react";
import { BetaFeatureRoute } from "@/components/beta-feature-route";
import { useSearch } from "wouter";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Trash2, Plus, RefreshCw, Pause, Play, Send, Calendar,
  AlertTriangle, CheckCircle2, Clock, Loader2, Truck, Info, CreditCard, DollarSign,
  ImageIcon, Camera, X,
} from "lucide-react";
import type { Property } from "@shared/schema";
import {
  TRASH_ON_DEMAND_CENTS,
  trashMonthlyFeeCents,
} from "@shared/fees";
import { usePageTitle } from "@/hooks/use-page-title";
import { sortProperties } from "@/lib/property-utils";
import TrashCoverageMap from "@/components/trash-coverage-map";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { BackToPropertyLink } from "@/components/back-to-property-link";

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

interface VendorCreditCard {
  id: number;
  brand: string | null;
  last4: string | null;
  nickname: string | null;
  isDefault: boolean;
}

interface PropertyCardAssignment {
  propertyId: number;
  vendorCreditCardId: number;
}

interface TrashCharge {
  id: number;
  propertyId: number | null;
  type: string;
  periodMonth: string | null;
  amountCents: number;
  status: string;
  createdAt: string;
}

function cardLabel(c: VendorCreditCard): string {
  const name = c.nickname || c.brand || "Card";
  const tail = c.last4 ? ` ····${c.last4}` : "";
  return `${name}${tail}${c.isDefault ? " (default)" : ""}`;
}

interface TrashEnrollment {
  id: number;
  orgId: number;
  propertyId: number | null;
  externalId: string | null;
  status: string;
  collectionDay: string;
  canCount: number;
  recycleEnabled: boolean;
  recycleDay: string | null;
  nextPickup: string | null;
  lastPickup: string | null;
  lastEvidencePhotoUrl: string | null;
  lastSyncedAt: string | null;
  monthlyServiceFeeCents: number | null;
  billingStartDate: string | null;
  billingStatus: string | null;
  nextBillingDate: string | null;
  vendorCreditCardId: number | null;
  createdAt: string;
  updatedAt: string;
}

interface TrashJob {
  id: number;
  enrollmentId: number | null;
  externalJobId: string | null;
  type: string;
  status: string;
  scheduledDate: string | null;
  completedAt: string | null;
  evidencePhotoUrl: string | null;
  notes: string | null;
  createdAt: string;
}

interface TrashRequest {
  id: number;
  propertyId: number | null;
  taskId: number | null;
  requestedBy: string;
  requestedDate: string | null;
  urgency: string | null;
  details: string | null;
  trashLocation: string | null;
  itemDescription: string | null;
  photoAttachmentUrl: string | null;
  emailStatus: string | null;
  createdAt: string;
}

const COLLECTION_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** August 1, 2025 — the date the trash collection service goes live. */
export const TRASH_LAUNCH_DATE = new Date("2025-08-01");

/**
 * Returns true while the current date is before the trash service launch on
 * August 1, 2025. When true, enrollment/on-demand actions are in "preview"
 * mode: the form is fully usable but submissions are interest-holds only —
 * no billing, no pickups until the launch date.
 */
export function isBeforeTrashLaunch(): boolean {
  return Date.now() < TRASH_LAUNCH_DATE.getTime();
}

/**
 * Pre-launch banner shown above the trash-collection tabs while the service
 * has not yet launched on August 1, 2025. Exported so tests can assert
 * against the real element without rendering the full page.
 */
export function TrashPrelaunchBanner() {
  if (!isBeforeTrashLaunch()) return null;
  return (
    <Alert className="mb-4 border-amber-300 bg-amber-50 text-amber-900" data-testid="banner-trash-prelaunch">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertDescription className="text-amber-900">
        <strong>Trash collection launches August 1, 2025.</strong>{" "}
        You can explore and queue properties now — everything saved before launch will be
        activated on August 1st. No billing or pickups will occur before that date.
      </AlertDescription>
    </Alert>
  );
}

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  active: "bg-green-100 text-green-800 border-green-200",
  paused: "bg-gray-100 text-gray-700 border-gray-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  failed: "bg-red-100 text-red-700 border-red-200",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  active: "Active",
  paused: "Paused",
  cancelled: "Cancelled",
  scheduled: "Scheduled",
  completed: "Completed",
  failed: "Failed",
  standard: "Standard",
  urgent: "Urgent",
  sent: "Sent",
  no_client: "Sent",
  failed_email: "Failed",
  pending_email: "Pending",
};

function StatusBadge({ status, label }: { status: string; label?: string }) {
  const cls = STATUS_BADGE[status] ?? "bg-gray-100 text-gray-700 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {label ?? STATUS_LABELS[status] ?? status}
    </span>
  );
}

function formatDate(val: string | null | undefined): string {
  if (!val) return "—";
  try {
    return new Date(val).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return val;
  }
}

function EvidenceThumbnail({
  url,
  label,
  onOpen,
  testId,
}: {
  url: string;
  label: string;
  onOpen: (p: { url: string; label: string }) => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen({ url, label });
      }}
      className="group relative h-10 w-10 flex-shrink-0 overflow-hidden rounded border border-gray-200 hover:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40"
      title="View collection-day evidence photo"
      data-testid={testId}
    >
      <img
        src={url}
        alt={`Collection evidence — ${label}`}
        className="h-full w-full object-cover transition-transform group-hover:scale-105"
        loading="lazy"
      />
    </button>
  );
}

/**
 * Pure helper: return only the properties with `active` or `launch` status.
 * Enrollment status is no longer required — serviceability is checked live
 * via the `check-serviceable` endpoint when a property is selected.
 *
 * Exported so unit tests can lock in this contract without mounting the full
 * page component.
 */
export function filterOnDemandEligibleProperties<P extends { id: number; status?: string | null }>(
  properties: P[],
): P[] {
  return properties.filter((p) => p.status === "active" || p.status === "launch");
}

/**
 * Presentational sub-component: the property <Select> + enrollment note inside
 * the on-demand pickup dialog.  Exported so tests can render just this piece
 * (with real Radix DOM) and assert dropdown contents and empty-state callout
 * without mounting the full page or mocking all of its data-fetching hooks.
 */
export function OnDemandPropertySelect({
  eligibleProperties,
  value,
  onValueChange,
}: {
  eligibleProperties: Array<{ id: number; address: string | null }>;
  value: string;
  onValueChange: (v: string) => void;
}) {
  return (
    <div>
      <Label htmlFor="od-property" className="mb-1.5 block">Property</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger id="od-property" data-testid="select-od-property">
          <SelectValue placeholder="Select a property" />
        </SelectTrigger>
        <SelectContent>
          {eligibleProperties.length === 0 ? (
            <div
              className="px-2 py-1.5 text-sm text-muted-foreground"
              data-testid="text-no-ondemand-properties"
            >
              No enrolled properties available.
            </div>
          ) : (
            eligibleProperties.map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>
                {p.address ?? ""}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      <div
        className="mt-2 flex items-start gap-2 p-2.5 bg-brand-muted text-brand-muted-foreground border border-brand-subtle rounded-lg text-sm"
        data-testid="text-od-enrollment-note"
      >
        <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>
          Only active and launch-status properties in a service area are eligible for on-demand pickup.
          {eligibleProperties.length === 0 && (
            <> No eligible properties found — make sure at least one property has Active or Launch status.</>
          )}
        </span>
      </div>
    </div>
  );
}

/**
 * Presentational sub-component: the On-Demand Requests history table.  Exported
 * so tests can render just this piece (with real DOM) and assert the per-request
 * cells — Trash Location, Description of Items, and the Photo thumbnail/link —
 * without mounting the full page or mocking all of its data-fetching hooks.
 */
export function OnDemandRequestsTable({
  requests,
  getPropertyLabel,
}: {
  requests: TrashRequest[];
  getPropertyLabel: (propertyId: number | null) => string;
}) {
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Property</TableHead>
              <TableHead>Requested By</TableHead>
              <TableHead>Requested Date</TableHead>
              <TableHead>Trash Location</TableHead>
              <TableHead>Description of Items</TableHead>
              <TableHead>Photo</TableHead>
              <TableHead>Urgency</TableHead>
              <TableHead>Email Status</TableHead>
              <TableHead>Submitted</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((r) => (
              <TableRow key={r.id} data-testid={`request-row-${r.id}`}>
                <TableCell>{getPropertyLabel(r.propertyId)}</TableCell>
                <TableCell>{r.requestedBy}</TableCell>
                <TableCell>{r.requestedDate ?? "ASAP"}</TableCell>
                <TableCell className="max-w-[200px]" data-testid={`text-request-location-${r.id}`}>
                  <span className="block truncate" title={r.trashLocation ?? undefined}>
                    {r.trashLocation || "—"}
                  </span>
                </TableCell>
                <TableCell className="max-w-[240px]" data-testid={`text-request-items-${r.id}`}>
                  <span className="block truncate" title={r.itemDescription ?? undefined}>
                    {r.itemDescription || "—"}
                  </span>
                </TableCell>
                <TableCell data-testid={`cell-request-photo-${r.id}`}>
                  {r.photoAttachmentUrl ? (
                    <a
                      href={r.photoAttachmentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block"
                      data-testid={`link-request-photo-${r.id}`}
                    >
                      <img
                        src={r.photoAttachmentUrl}
                        alt="Trash request attachment"
                        className="h-10 w-10 rounded object-cover border border-border hover-elevate"
                        loading="lazy"
                      />
                    </a>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <StatusBadge
                    status={r.urgency ?? "standard"}
                    label={r.urgency === "urgent" ? "Urgent" : "Standard"}
                  />
                </TableCell>
                <TableCell>
                  <StatusBadge
                    status={r.emailStatus ?? "pending"}
                    label={
                      r.emailStatus === "sent" || r.emailStatus === "no_client"
                        ? "Sent"
                        : r.emailStatus === "failed"
                        ? "Failed"
                        : "Pending"
                    }
                  />
                </TableCell>
                <TableCell>{formatDate(r.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export interface OnDemandFormState {
  propertyId: string;
  requestedDate: string;
  urgency: "standard" | "urgent";
  details: string;
  trashLocation: string;
  itemDescription: string;
  photoAttachmentUrl: string | null;
  taskId: number | null;
  vendorCreditCardId: string;
  acceptBilling: boolean;
}

interface ServiceabilityResult {
  serviceable: boolean;
  _noCoords?: boolean;
  _message?: string;
}

/**
 * Submit-button gate for the on-demand pickup dialog. Returns true when the
 * "Pay & Schedule" button must stay disabled. Exported so the
 * billing-acceptance gate (and every other required field) can be locked in by
 * tests without mounting the whole page.
 */
export function isOnDemandSubmitDisabled(args: {
  isPending: boolean;
  photoUploading: boolean;
  serviceabilityFetching: boolean;
  isPropertyEligible: boolean;
  form: Pick<
    OnDemandFormState,
    "propertyId" | "trashLocation" | "itemDescription" | "acceptBilling" | "vendorCreditCardId"
  >;
}): boolean {
  const { isPending, photoUploading, serviceabilityFetching, isPropertyEligible, form } = args;
  return (
    isPending ||
    photoUploading ||
    !form.propertyId ||
    !isPropertyEligible ||
    serviceabilityFetching ||
    !form.trashLocation.trim() ||
    !form.itemDescription.trim() ||
    !form.acceptBilling ||
    !form.vendorCreditCardId
  );
}

/**
 * Reads a file as a base64 string (the part after the data-URL comma), exactly
 * as the on-demand photo upload expects. Split out so the upload handler can
 * inject a deterministic encoder in tests instead of relying on FileReader.
 */
function readFileAsBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Handles a photo-file selection for the on-demand pickup dialog: reads the
 * file as base64, POSTs it to `/api/app/upload`, and stores the returned
 * `imageUrl` on the form. On any failure it surfaces a destructive toast and
 * leaves the form untouched. `photoUploading` is always toggled on at the
 * start and reset to false in `finally`, so the form never gets stuck in the
 * "uploading" state. Exported (with injectable deps) so both the success and
 * failure paths can be tested without mounting the page.
 */
export async function handleOnDemandPhotoUpload(args: {
  file: File | null | undefined;
  request: typeof apiRequest;
  toast: (opts: { title: string; description: string; variant?: "destructive" }) => void;
  setPhotoUploading: (uploading: boolean) => void;
  setForm: React.Dispatch<React.SetStateAction<OnDemandFormState>>;
  fileToBase64?: (file: File) => Promise<string>;
}): Promise<void> {
  const { file, request, toast, setPhotoUploading, setForm } = args;
  if (!file) return;
  setPhotoUploading(true);
  try {
    const base64 = await (args.fileToBase64 ?? readFileAsBase64)(file);
    const resp = await request("POST", "/api/app/upload", {
      filename: file.name,
      mimeType: file.type,
      data: base64,
    });
    const body = await resp.json();
    setForm((p) => ({ ...p, photoAttachmentUrl: body.imageUrl ?? null }));
  } catch {
    toast({
      title: "Photo upload failed",
      description: "Could not upload the photo. Please try again.",
      variant: "destructive",
    });
  } finally {
    setPhotoUploading(false);
  }
}

/**
 * Builds the request body sent to `POST /api/app/trash/on-demand`. Exported so
 * the submission payload (property/urgency coercion, trim-free passthrough,
 * card parsing) can be asserted directly by tests.
 */
export function buildOnDemandPayload(form: OnDemandFormState) {
  return {
    propertyId: parseInt(form.propertyId),
    requestedDate: form.requestedDate || null,
    urgency: form.urgency,
    details: form.details || null,
    trashLocation: form.trashLocation,
    itemDescription: form.itemDescription,
    photoAttachmentUrl: form.photoAttachmentUrl ?? null,
    taskId: form.taskId,
    vendorCreditCardId: form.vendorCreditCardId ? parseInt(form.vendorCreditCardId, 10) : null,
    acceptBilling: form.acceptBilling,
  };
}

/**
 * Presentational sub-component: the full On-Demand pickup request dialog (the
 * submission flow that complements the history table). Exported so tests can
 * render just this piece with real DOM — pick a property, fill the trash
 * location / item description, simulate a photo upload, toggle the
 * billing-acceptance gate, and assert the submit handler fires — without
 * mounting the full page or mocking all of its data-fetching hooks.
 */
export function OnDemandRequestDialog({
  open,
  onOpenChange,
  form,
  setForm,
  eligibleProperties,
  serviceabilityFetching,
  serviceabilityData,
  isPropertyEligible,
  vendorCards,
  photoUploading,
  onPhotoChange,
  onSubmit,
  onCancel,
  isPending,
  assignedCardIdFor,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: OnDemandFormState;
  setForm: React.Dispatch<React.SetStateAction<OnDemandFormState>>;
  eligibleProperties: Array<{ id: number; address: string | null }>;
  serviceabilityFetching: boolean;
  serviceabilityData: ServiceabilityResult | undefined;
  isPropertyEligible: boolean;
  vendorCards: VendorCreditCard[];
  photoUploading: boolean;
  onPhotoChange: React.ChangeEventHandler<HTMLInputElement>;
  onSubmit: () => void;
  onCancel: () => void;
  isPending: boolean;
  assignedCardIdFor: (propertyId: string) => string;
}) {
  // Gate dismissal on any in-flight work — both the submit mutation and an
  // in-progress photo upload — so a stray Escape / outside click never drops a
  // photo that's still uploading. See the "long-running dialogs gate dismissal
  // on in-flight state" convention in replit.md.
  const busy = isPending || photoUploading;
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (busy) return;
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="flex flex-col max-h-[90vh]"
        onInteractOutside={(e) => {
          if (busy) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (busy) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (busy) e.preventDefault();
        }}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Request On-Demand Pickup</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-1 -mr-1">
          {form.taskId != null && (
            <div className="flex items-center gap-2 p-2.5 bg-brand-muted text-brand-muted-foreground border border-brand-subtle rounded-lg text-sm" data-testid="text-od-linked-task">
              <Info className="h-4 w-4 flex-shrink-0" />
              Linked to maintenance task #{form.taskId}.
            </div>
          )}
          <OnDemandPropertySelect
            eligibleProperties={eligibleProperties}
            value={form.propertyId}
            onValueChange={(v) =>
              setForm((p) => ({
                ...p,
                propertyId: v,
                vendorCreditCardId: assignedCardIdFor(v) || p.vendorCreditCardId,
              }))
            }
          />

          {/* Serviceability check result */}
          {form.propertyId && serviceabilityFetching && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-serviceable-loading">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking service area…
            </div>
          )}
          {form.propertyId && !serviceabilityFetching && serviceabilityData && !serviceabilityData.serviceable && (
            <div
              className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800"
              data-testid="text-not-serviceable"
            >
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {serviceabilityData._noCoords
                ? "Address coordinates not found — re-save the property on the property detail page to geocode it."
                : "This property is outside our current service area and cannot be scheduled for an on-demand pickup."}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="od-date" className="mb-1.5 block">Preferred Date (optional)</Label>
              <Input
                id="od-date"
                type="date"
                value={form.requestedDate}
                onChange={(e) => setForm((p) => ({ ...p, requestedDate: e.target.value }))}
                data-testid="input-od-date"
              />
            </div>
            <div>
              <Label htmlFor="od-urgency" className="mb-1.5 block">Urgency</Label>
              <Select
                value={form.urgency}
                onValueChange={(v: "standard" | "urgent") =>
                  setForm((p) => ({ ...p, urgency: v }))
                }
              >
                <SelectTrigger id="od-urgency" data-testid="select-od-urgency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.urgency === "urgent" && (
            <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              Urgent requests are prioritized and may incur additional fees.
            </div>
          )}

          <div>
            <Label htmlFor="od-trash-location" className="mb-1.5 block">
              Trash location <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="od-trash-location"
              placeholder="e.g. side gate, alley behind unit 3"
              value={form.trashLocation}
              onChange={(e) => setForm((p) => ({ ...p, trashLocation: e.target.value }))}
              rows={2}
              data-testid="textarea-od-trash-location"
            />
          </div>

          <div>
            <Label htmlFor="od-item-description" className="mb-1.5 block">
              Description of items <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="od-item-description"
              placeholder="What needs to be picked up?"
              value={form.itemDescription}
              onChange={(e) => setForm((p) => ({ ...p, itemDescription: e.target.value }))}
              rows={3}
              data-testid="textarea-od-item-description"
            />
          </div>

          <div>
            <Label htmlFor="od-photo" className="mb-1.5 block flex items-center gap-1.5">
              <Camera className="h-3.5 w-3.5 text-muted-foreground" />
              Photo (optional)
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="od-photo"
                type="file"
                accept="image/*"
                disabled={photoUploading || isPending}
                onChange={onPhotoChange}
                data-testid="input-od-photo"
                className="text-sm"
              />
              {photoUploading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground flex-shrink-0" />}
              {form.photoAttachmentUrl && !photoUploading && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive flex-shrink-0"
                  onClick={() => setForm((p) => ({ ...p, photoAttachmentUrl: null }))}
                  title="Remove photo"
                  data-testid="button-od-remove-photo"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {form.photoAttachmentUrl && (
              <p className="text-xs text-green-700 mt-1 flex items-center gap-1" data-testid="text-od-photo-uploaded">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Photo uploaded
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="od-details" className="mb-1.5 block">Additional notes (optional)</Label>
            <Textarea
              id="od-details"
              placeholder="Any other special instructions..."
              value={form.details}
              onChange={(e) => setForm((p) => ({ ...p, details: e.target.value }))}
              rows={2}
              data-testid="textarea-od-details"
            />
          </div>

          {/* Billing: flat one-time charge taken immediately on confirm */}
          <div className="border-t pt-4 space-y-3">
            <div>
              <Label htmlFor="od-card" className="mb-1.5 block flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                Billing card
              </Label>
              {vendorCards.length === 0 ? (
                <div className="text-sm text-muted-foreground p-2.5 bg-muted rounded-lg" data-testid="text-no-cards-od">
                  No cards on file. Add a card in Vendor Settings before scheduling.
                </div>
              ) : (
                <Select
                  value={form.vendorCreditCardId}
                  onValueChange={(v) => setForm((p) => ({ ...p, vendorCreditCardId: v }))}
                >
                  <SelectTrigger id="od-card" data-testid="select-od-card">
                    <SelectValue placeholder="Select a card" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendorCards.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{cardLabel(c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="p-3 bg-brand-muted text-brand-muted-foreground border border-brand-subtle rounded-lg text-sm flex items-center gap-1.5 font-medium" data-testid="text-od-billing-terms">
              <DollarSign className="h-4 w-4" />
              {formatCents(TRASH_ON_DEMAND_CENTS)} one-time charge, billed immediately when you schedule.
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="label-accept-od-billing">
              <Checkbox
                checked={form.acceptBilling}
                onCheckedChange={(v) => setForm((p) => ({ ...p, acceptBilling: v === true }))}
                className="mt-0.5"
                data-testid="checkbox-accept-od-billing"
              />
              <span className="text-sm text-foreground">
                I authorize PadPulse to charge the selected card {formatCents(TRASH_ON_DEMAND_CENTS)} now
                for this on-demand pickup.
              </span>
            </label>
          </div>
        </div>

        {isBeforeTrashLaunch() && (
          <div
            className="flex items-start gap-2 p-3 mb-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex-shrink-0"
            data-testid="text-od-prelaunch-note"
          >
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
            <span>
              On-demand pickups will be available starting <strong>August 1, 2025</strong>.
              This button will be enabled on launch day.
            </span>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t flex-shrink-0">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
            data-testid="button-cancel-od"
          >
            Cancel
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    className="bg-brand text-white hover:bg-brand-hover"
                    onClick={onSubmit}
                    disabled={
                      isOnDemandSubmitDisabled({
                        isPending,
                        photoUploading,
                        serviceabilityFetching,
                        isPropertyEligible,
                        form,
                      }) || isBeforeTrashLaunch()
                    }
                    data-testid="button-submit-od"
                  >
                    {isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    ) : (
                      <Send className="h-4 w-4 mr-1.5" />
                    )}
                    Pay {formatCents(TRASH_ON_DEMAND_CENTS)} &amp; Schedule
                  </Button>
                </span>
              </TooltipTrigger>
              {isBeforeTrashLaunch() && (
                <TooltipContent>
                  On-demand pickups are available starting August 1, 2025
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** State shape for the property enrollment form. Exported for tests. */
export interface EnrollFormState {
  propertyId: string;
  collectionDay: string;
  canCount: number;
  recycleEnabled: boolean;
  recycleDay: string;
  vendorCreditCardId: string;
  acceptBilling: boolean;
}

/**
 * Enrollment dialog extracted as a named component so tests can render it in
 * isolation (same pattern as OnDemandRequestDialog) and assert the real
 * button-submit-enroll disabled/enabled state without mocking the full page.
 */
export function EnrollmentDialog({
  open,
  onOpenChange,
  form,
  setForm,
  enrollableProperties,
  vendorCards,
  assignedCardIdFor,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: EnrollFormState;
  setForm: React.Dispatch<React.SetStateAction<EnrollFormState>>;
  enrollableProperties: Array<{ id: number; address: string | null }>;
  vendorCards: VendorCreditCard[];
  assignedCardIdFor: (propertyId: string) => string;
  onSubmit: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (isPending) return;
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="flex flex-col max-h-[90vh]"
        onInteractOutside={(e) => {
          if (isPending) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isPending) e.preventDefault();
        }}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Enroll Property in Collection Service</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-1 -mr-1">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Label htmlFor="enroll-property">Property</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-gray-400" data-testid="tooltip-enroll-property-info" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Only properties with an <strong>Active</strong> or <strong>Launch</strong> status
                    can be enrolled in scheduled collection service.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Select
              value={form.propertyId}
              onValueChange={(v) => setForm((p) => ({ ...p, propertyId: v, vendorCreditCardId: assignedCardIdFor(v) || p.vendorCreditCardId }))}
            >
              <SelectTrigger id="enroll-property" data-testid="select-enroll-property">
                <SelectValue placeholder="Select a property" />
              </SelectTrigger>
              <SelectContent>
                {enrollableProperties.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground" data-testid="text-no-enrollable-properties">
                    No active or launch-status properties available.
                  </div>
                ) : (
                  enrollableProperties.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.address}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="enroll-collection-day" className="mb-1.5 block">Collection Day</Label>
              <Select
                value={form.collectionDay}
                onValueChange={(v) => setForm((p) => ({ ...p, collectionDay: v }))}
              >
                <SelectTrigger id="enroll-collection-day" data-testid="select-collection-day">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLLECTION_DAYS.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="enroll-can-count" className="mb-1.5 block">Number of Cans</Label>
              <Input
                id="enroll-can-count"
                type="number"
                min={1}
                max={20}
                value={form.canCount}
                onChange={(e) =>
                  setForm((p) => ({ ...p, canCount: Math.max(1, parseInt(e.target.value) || 1) }))
                }
                data-testid="input-can-count"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="enroll-recycle"
              checked={form.recycleEnabled}
              onCheckedChange={(v) => setForm((p) => ({ ...p, recycleEnabled: v }))}
              data-testid="switch-recycle-enabled"
            />
            <Label htmlFor="enroll-recycle">Add 2nd weekly pickup</Label>
          </div>

          {form.recycleEnabled && (
            <div>
              <Label htmlFor="enroll-recycle-day" className="mb-1.5 block">Second PickUp Day</Label>
              <Select
                value={form.recycleDay || "Monday"}
                onValueChange={(v) => setForm((p) => ({ ...p, recycleDay: v }))}
              >
                <SelectTrigger id="enroll-recycle-day" data-testid="select-recycle-day">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLLECTION_DAYS.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Billing: card picker + plain-language proration terms + accept */}
          <div className="border-t pt-4 space-y-3">
            <div>
              <Label htmlFor="enroll-card" className="mb-1.5 block flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                Billing card
              </Label>
              {vendorCards.length === 0 ? (
                <div className="text-sm text-muted-foreground p-2.5 bg-muted rounded-lg" data-testid="text-no-cards-enroll">
                  No cards on file. Add a card in Vendor Settings before enrolling.
                </div>
              ) : (
                <Select
                  value={form.vendorCreditCardId}
                  onValueChange={(v) => setForm((p) => ({ ...p, vendorCreditCardId: v }))}
                >
                  <SelectTrigger id="enroll-card" data-testid="select-enroll-card">
                    <SelectValue placeholder="Select a card" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendorCards.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{cardLabel(c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {form.propertyId && assignedCardIdFor(form.propertyId) === form.vendorCreditCardId && form.vendorCreditCardId ? (
                <p className="text-xs text-muted-foreground mt-1">Using this property's connected Vendor Settings card.</p>
              ) : null}
            </div>

            <div className="p-3 bg-brand-muted text-brand-muted-foreground border border-brand-subtle rounded-lg text-sm space-y-1.5" data-testid="text-enroll-billing-terms">
              <div className="flex items-center gap-1.5 font-medium">
                <DollarSign className="h-4 w-4" />
                {formatCents(trashMonthlyFeeCents(form.recycleEnabled))}/month
                {form.recycleEnabled ? " (includes 2nd weekly pickup)" : ""}
              </div>
              <p>
                No charge today. Billing starts the day the provider <strong>approves</strong> this
                property — your first charge is <strong>prorated</strong> for the remaining days of that
                month, then the full {formatCents(trashMonthlyFeeCents(form.recycleEnabled))} bills
                on the 1st of each month after. Each property bills separately, and pausing a property
                pauses its billing.
              </p>
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="label-accept-enroll-billing">
              <Checkbox
                checked={form.acceptBilling}
                onCheckedChange={(v) => setForm((p) => ({ ...p, acceptBilling: v === true }))}
                className="mt-0.5"
                data-testid="checkbox-accept-enroll-billing"
              />
              <span className="text-sm text-foreground">
                I authorize PadPulse to charge the selected card the recurring monthly service fee as
                described above, starting when the property is approved.
              </span>
            </label>
          </div>
        </div>

        {isBeforeTrashLaunch() && (
          <div
            className="flex items-start gap-2 p-3 mb-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"
            data-testid="text-enroll-prelaunch-note"
          >
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
            <span>
              This enrollment will be saved as an <strong>interest hold</strong> and
              activated automatically on <strong>August 1, 2025</strong>. No billing
              or collection will occur before that date.
            </span>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            data-testid="button-cancel-enroll"
          >
            Cancel
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    className="bg-brand text-white hover:bg-brand-hover"
                    onClick={onSubmit}
                    disabled={
                      isPending ||
                      !form.propertyId ||
                      !form.acceptBilling ||
                      !form.vendorCreditCardId ||
                      isBeforeTrashLaunch()
                    }
                    data-testid="button-submit-enroll"
                  >
                    {isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    ) : null}
                    {isBeforeTrashLaunch() ? "Queue for Aug 1" : "Enroll"}
                  </Button>
                </span>
              </TooltipTrigger>
              {isBeforeTrashLaunch() && (
                <TooltipContent>
                  Enrollment activates automatically on August 1, 2025
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AppOperationsTrash() {
  return <BetaFeatureRoute featureKey="trash-service"><TrashServicePage /></BetaFeatureRoute>;
}

function TrashServicePage() {
  const queryClient = useQueryClient();
  usePageTitle("Trash Service");
  const { toast } = useToast();
  const { canEdit } = usePermissions();
  const isViewOnly = !canEdit("maintenance");

  const search = useSearch();
  const backToPropertyId = useMemo(
    () => new URLSearchParams(search).get("propertyId"),
    [search],
  );

  const [activeTab, setActiveTab] = useState("enrollments");
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [showOnDemandDialog, setShowOnDemandDialog] = useState(false);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<number | null>(null);
  const [jobsPropertyFilter, setJobsPropertyFilter] = useListState<string>("trash:property", "all");
  const [lightboxPhoto, setLightboxPhoto] = useState<{ url: string; label: string } | null>(null);

  const [enrollForm, setEnrollForm] = useState({
    propertyId: "" as string,
    collectionDay: "Monday",
    canCount: 1,
    recycleEnabled: false,
    recycleDay: "",
    vendorCreditCardId: "" as string,
    acceptBilling: false,
  });

  const [onDemandForm, setOnDemandForm] = useState({
    propertyId: "" as string,
    requestedDate: "",
    urgency: "standard" as "standard" | "urgent",
    details: "",
    trashLocation: "",
    itemDescription: "",
    photoAttachmentUrl: null as string | null,
    taskId: null as number | null,
    vendorCreditCardId: "" as string,
    acceptBilling: false,
  });
  const [photoUploading, setPhotoUploading] = useState(false);

  // Deep-link handling: property-detail Trash subtab links to
  // `?tab=jobs&propertyId=N`; the task detail "on-demand pickup" button links to
  // `?onDemand=1&propertyId=N&taskId=N`. Parse once whenever the search changes.
  useEffect(() => {
    const params = new URLSearchParams(search);
    const tab = params.get("tab");
    const propertyId = params.get("propertyId");
    const openOnDemand = params.get("onDemand");
    const taskId = params.get("taskId");

    if (openOnDemand === "1") {
      setOnDemandForm((p) => ({
        ...p,
        propertyId: propertyId ?? "",
        taskId: taskId ? parseInt(taskId, 10) : null,
      }));
      setShowOnDemandDialog(true);
    }
    if (tab === "jobs" || tab === "enrollments" || tab === "requests") {
      setActiveTab(tab);
    }
    // Pre-filter the Jobs tab when deep-linked from a property's Trash subtab.
    if (openOnDemand !== "1" && propertyId) {
      setJobsPropertyFilter(propertyId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
    staleTime: 60_000,
  });

  const enrollmentsQuery = useQuery<TrashEnrollment[]>({
    queryKey: ["/api/app/trash/enrollments"],
  });

  const jobsQuery = useQuery<TrashJob[]>({
    queryKey: ["/api/app/trash/jobs"],
    enabled: activeTab === "jobs",
  });

  const requestsQuery = useQuery<TrashRequest[]>({
    queryKey: ["/api/app/trash/requests"],
    enabled: activeTab === "requests",
  });

  const enrollmentJobsQuery = useQuery<TrashJob[]>({
    queryKey: ["/api/app/trash/enrollments", selectedEnrollmentId, "jobs"],
    queryFn: () =>
      fetch(`/api/app/trash/enrollments/${selectedEnrollmentId}/jobs`, { credentials: "include" })
        .then((r) => r.json()),
    enabled: selectedEnrollmentId !== null,
  });

  const cardsQuery = useQuery<VendorCreditCard[]>({
    queryKey: ["/api/app/operations/vendor-credit-cards"],
    staleTime: 60_000,
  });
  const assignmentsQuery = useQuery<PropertyCardAssignment[]>({
    queryKey: ["/api/app/operations/property-card-assignments"],
    staleTime: 60_000,
  });
  const chargesQuery = useQuery<TrashCharge[]>({
    queryKey: ["/api/app/trash/charges"],
    enabled: activeTab === "billing",
  });

  const vendorCards = cardsQuery.data ?? [];
  // The card connected to a property in Vendor Settings is the default
  // suggestion; the host can override it with the dropdown.
  function assignedCardIdFor(propertyId: string): string {
    if (!propertyId) return "";
    const a = (assignmentsQuery.data ?? []).find((x) => x.propertyId === parseInt(propertyId, 10));
    return a ? String(a.vendorCreditCardId) : "";
  }

  const sortedProperties = sortProperties(propertiesQuery.data ?? []);
  // Only active / launch-status properties can be enrolled in scheduled service.
  const enrollableProperties = sortedProperties.filter(
    (p) => p.status === "active" || p.status === "launch",
  );

  const createEnrollmentMutation = useMutation({
    mutationFn: (data: typeof enrollForm) =>
      apiRequest("POST", "/api/app/trash/enrollments", {
        propertyId: parseInt(data.propertyId),
        collectionDay: data.collectionDay,
        canCount: data.canCount,
        recycleEnabled: data.recycleEnabled,
        recycleDay: data.recycleEnabled && data.recycleDay ? data.recycleDay : null,
        vendorCreditCardId: data.vendorCreditCardId ? parseInt(data.vendorCreditCardId, 10) : null,
        acceptBilling: data.acceptBilling,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/trash/enrollments"] });
      setShowEnrollDialog(false);
      setEnrollForm({ propertyId: "", collectionDay: "Monday", canCount: 1, recycleEnabled: false, recycleDay: "", vendorCreditCardId: "", acceptBilling: false });
      if (isBeforeTrashLaunch()) {
        toast({
          title: "Interest hold saved",
          description: "Your enrollment has been queued and will activate automatically on August 1, 2025. No billing or pickups will occur before that date.",
        });
      } else {
        toast({ title: "Enrollment created", description: "Property enrolled. Billing begins when the provider approves it." });
      }
    },
    onError: (err: any) => {
      const msg = parseErrorMessage(err, "Couldn't enroll this property. Please try again.");
      toast({ title: "Enrollment failed", description: msg, variant: "destructive" });
    },
  });

  const pauseMutation = useMutation({
    mutationFn: (id: number) => apiRequest("POST", `/api/app/trash/enrollments/${id}/pause`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/trash/enrollments"] });
      toast({ title: "Enrollment paused" });
    },
    onError: (err: any) => toast({ title: "Couldn't pause enrollment", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const resumeMutation = useMutation({
    mutationFn: (id: number) => apiRequest("POST", `/api/app/trash/enrollments/${id}/resume`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/trash/enrollments"] });
      toast({ title: "Enrollment resumed" });
    },
    onError: (err: any) => toast({ title: "Couldn't resume enrollment", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const onDemandMutation = useMutation({
    mutationFn: (data: typeof onDemandForm) =>
      apiRequest("POST", "/api/app/trash/on-demand", {
        propertyId: parseInt(data.propertyId),
        requestedDate: data.requestedDate || null,
        urgency: data.urgency,
        details: data.details || null,
        trashLocation: data.trashLocation,
        itemDescription: data.itemDescription,
        photoAttachmentUrl: data.photoAttachmentUrl ?? null,
        taskId: data.taskId,
        vendorCreditCardId: data.vendorCreditCardId ? parseInt(data.vendorCreditCardId, 10) : null,
        acceptBilling: data.acceptBilling,
      }),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/trash/requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/trash/charges"] });
      setShowOnDemandDialog(false);
      setOnDemandForm({ propertyId: "", requestedDate: "", urgency: "standard", details: "", trashLocation: "", itemDescription: "", photoAttachmentUrl: null, taskId: null, vendorCreditCardId: "", acceptBilling: false });
      const charged = res?.paymentStatus === "succeeded";
      toast({
        title: res?.alreadySent ? "Already requested" : "On-demand pickup scheduled",
        description: res?.alreadySent
          ? "This task already has a request on file — no additional charge."
          : charged
          ? `Your on-demand pickup was scheduled and ${formatCents(TRASH_ON_DEMAND_CENTS)} was charged.`
          : `Your on-demand pickup was scheduled. (Test mode — no charge was created.)`,
      });
    },
    onError: (err: any) => toast({ title: "Request failed", description: parseErrorMessage(err, "Couldn't schedule this pickup. Please try again."), variant: "destructive" }),
  });

  const propertyMap = new Map<number, Property>(
    (propertiesQuery.data ?? []).map((p) => [p.id, p]),
  );

  function getPropertyLabel(propertyId: number | null): string {
    if (!propertyId) return "—";
    const p = propertyMap.get(propertyId);
    return p?.address ?? `Property #${propertyId}`;
  }

  // Jobs have no direct propertyId; resolve it through their enrollment.
  const enrollmentPropertyMap = new Map<number, number | null>(
    (enrollmentsQuery.data ?? []).map((e) => [e.id, e.propertyId]),
  );

  // Properties that actually have trash jobs / enrollments, for the Jobs filter.
  const enrolledPropertyIds = Array.from(
    new Set(
      (enrollmentsQuery.data ?? [])
        .map((e) => e.propertyId)
        .filter((id): id is number => id != null),
    ),
  );

  // On-demand pickup is available for any active or launch-status property;
  // serviceability is confirmed live via the check-serviceable endpoint.
  const onDemandEligibleProperties = filterOnDemandEligibleProperties(sortedProperties);

  const isOnDemandPropertyInList =
    !!onDemandForm.propertyId &&
    onDemandEligibleProperties.some((p) => String(p.id) === onDemandForm.propertyId);

  // Serviceability check — fires when the host picks a property.
  const serviceabilityQuery = useQuery<{ serviceable: boolean; routeId?: number }>({
    queryKey: ["/api/app/trash/check-serviceable", onDemandForm.propertyId],
    queryFn: () =>
      fetch(`/api/app/trash/check-serviceable?propertyId=${onDemandForm.propertyId}`, {
        credentials: "include",
      }).then(async (r) => {
        if (r.status === 400) {
          const body = await r.json().catch(() => ({}));
          return { serviceable: false, _noCoords: true, _message: body.message } as any;
        }
        if (!r.ok) throw new Error("Serviceability check failed");
        return r.json();
      }),
    enabled: isOnDemandPropertyInList,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const isServiceable = serviceabilityQuery.data?.serviceable === true;
  const isOnDemandPropertyEligible = isOnDemandPropertyInList && isServiceable;

  // A deep-link (`?onDemand=1&propertyId=N`) can prefill a property before the
  // properties query resolves, or for a property that isn't eligible.
  // Once property data is available, drop any selection that isn't in the list.
  useEffect(() => {
    if (propertiesQuery.isLoading) return;
    if (onDemandForm.propertyId && !isOnDemandPropertyInList) {
      setOnDemandForm((p) => ({ ...p, propertyId: "" }));
    }
  }, [propertiesQuery.isLoading, onDemandForm.propertyId, isOnDemandPropertyInList]);

  const filteredJobs = (jobsQuery.data ?? []).filter((job) => {
    if (jobsPropertyFilter === "all") return true;
    if (job.enrollmentId == null) return false;
    return String(enrollmentPropertyMap.get(job.enrollmentId) ?? "") === jobsPropertyFilter;
  });

  const selectedEnrollment = enrollmentsQuery.data?.find((e) => e.id === selectedEnrollmentId);

  // Coverage-map quick actions: pre-fill and open the matching flow for an
  // in-area, eligible property clicked straight from its map marker.
  function handleMapEnroll(property: Property) {
    const id = String(property.id);
    setShowOnDemandDialog(false);
    setEnrollForm({
      propertyId: id,
      collectionDay: "Monday",
      canCount: 1,
      recycleEnabled: false,
      recycleDay: "",
      vendorCreditCardId: assignedCardIdFor(id),
      acceptBilling: false,
    });
    setShowEnrollDialog(true);
  }

  // Coverage-map enrollment lookup: propertyId -> current enrollment so the
  // map can show each marker's status (active / paused / pending) and offer the
  // matching action instead of a misleading "Enroll" button. Cancelled
  // enrollments are skipped so the host can re-enroll. When a property has more
  // than one row, prefer active > paused > pending.
  const enrollmentByPropertyId = useMemo(() => {
    const priority: Record<string, number> = { active: 3, paused: 2, pending: 1 };
    const map: Record<number, { id: number; status: string }> = {};
    for (const e of enrollmentsQuery.data ?? []) {
      if (e.propertyId == null || e.status === "cancelled") continue;
      const existing = map[e.propertyId];
      if (!existing || (priority[e.status] ?? 0) > (priority[existing.status] ?? 0)) {
        map[e.propertyId] = { id: e.id, status: e.status };
      }
    }
    return map;
  }, [enrollmentsQuery.data]);

  function handleMapPause(_property: Property, enrollment: { id: number; status: string }) {
    pauseMutation.mutate(enrollment.id);
  }

  function handleMapResume(_property: Property, enrollment: { id: number; status: string }) {
    resumeMutation.mutate(enrollment.id);
  }

  function handleMapRequestPickup(property: Property) {
    const id = String(property.id);
    setShowEnrollDialog(false);
    setOnDemandForm({
      propertyId: id,
      requestedDate: "",
      urgency: "standard",
      details: "",
      trashLocation: "",
      itemDescription: "",
      photoAttachmentUrl: null,
      taskId: null,
      vendorCreditCardId: assignedCardIdFor(id),
      acceptBilling: false,
    });
    setShowOnDemandDialog(true);
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {backToPropertyId ? (
          <BackToPropertyLink propertyId={backToPropertyId} tab="trash" />
        ) : null}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Trash2 className="h-6 w-6 text-brand" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-tour="trash-heading">Trash Service</h1>
              <p className="text-sm text-gray-500">
                Manage scheduled collection and on-demand pickup requests
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isViewOnly && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOnDemandDialog(true)}
                  data-testid="button-on-demand-request"
                  data-tour="trash-on-demand"
                >
                  <Truck className="h-4 w-4 mr-1.5" />
                  On-Demand
                </Button>
                <Button
                  size="sm"
                  className="bg-brand text-white hover:bg-brand-hover"
                  onClick={() => setShowEnrollDialog(true)}
                  data-testid="button-enroll-property"
                  data-tour="trash-enroll"
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  Enroll Property
                </Button>
              </>
            )}
            <ExplainerVideoButton pageKey="trash" />
            <GuideButton pageKey="trash" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="trash" />

        <TrashPrelaunchBanner />

        <ViewOnlyBanner area="maintenance" />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4" data-tour="trash-tabs">
            <TabsTrigger value="enrollments" data-testid="tab-enrollments">Enrollments</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests">On-Demand Requests</TabsTrigger>
            <TabsTrigger value="coverage" data-testid="tab-coverage">Coverage Map</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
          </TabsList>

          {/* Enrollments */}
          <TabsContent value="enrollments" data-tour="trash-enrollments">
            {enrollmentsQuery.isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-24 rounded-lg" />
                ))}
              </div>
            ) : !enrollmentsQuery.data?.length ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <Trash2 className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No properties enrolled</p>
                  <p className="text-sm mt-1">Enroll a property to start scheduled collection service.</p>
                  {!isViewOnly && (
                    <Button
                      className="mt-4 bg-brand text-white hover:bg-brand-hover"
                      size="sm"
                      onClick={() => setShowEnrollDialog(true)}
                      data-testid="button-enroll-first"
                    >
                      <Plus className="h-4 w-4 mr-1.5" />
                      Enroll Property
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {enrollmentsQuery.data.map((enrollment) => (
                  <Card
                    key={enrollment.id}
                    className={`cursor-pointer transition-colors hover:border-brand/40 ${
                      selectedEnrollmentId === enrollment.id ? "border-brand ring-1 ring-brand/20" : ""
                    }`}
                    onClick={() =>
                      setSelectedEnrollmentId(
                        selectedEnrollmentId === enrollment.id ? null : enrollment.id,
                      )
                    }
                    data-testid={`card-enrollment-${enrollment.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900 truncate">
                              {getPropertyLabel(enrollment.propertyId)}
                            </span>
                            {isBeforeTrashLaunch() && enrollment.status === "pending" ? (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-amber-100 text-amber-800 border-amber-200"
                                data-testid={`badge-pre-launch-${enrollment.id}`}
                              >
                                Pending — Launches Aug 1
                              </span>
                            ) : (
                              <StatusBadge status={enrollment.status} />
                            )}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />
                              {enrollment.collectionDay}s
                              {enrollment.recycleEnabled && enrollment.recycleDay
                                ? ` · 2nd weekly pickup: ${enrollment.recycleDay}s`
                                : ""}
                            </span>
                            <span>{enrollment.canCount} can{enrollment.canCount !== 1 ? "s" : ""}</span>
                            {enrollment.nextPickup && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                Next: {formatDate(enrollment.nextPickup)}
                              </span>
                            )}
                            {enrollment.lastPickup && (
                              <span className="flex items-center gap-1">
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                Last: {formatDate(enrollment.lastPickup)}
                              </span>
                            )}
                          </div>
                        </div>
                        {enrollment.lastEvidencePhotoUrl && (
                          <div className="flex flex-shrink-0 flex-col items-center gap-1">
                            <EvidenceThumbnail
                              url={enrollment.lastEvidencePhotoUrl}
                              label={`${getPropertyLabel(enrollment.propertyId)} — last pickup ${formatDate(enrollment.lastPickup)}`}
                              onOpen={setLightboxPhoto}
                              testId={`thumb-enrollment-evidence-${enrollment.id}`}
                            />
                            <span className="text-[10px] text-gray-400">Evidence</span>
                          </div>
                        )}
                        {!isViewOnly && (
                          <div className="flex gap-2 flex-shrink-0">
                            {enrollment.status === "active" ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  pauseMutation.mutate(enrollment.id);
                                }}
                                disabled={pauseMutation.isPending}
                                data-testid={`button-pause-${enrollment.id}`}
                              >
                                {pauseMutation.isPending ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Pause className="h-3.5 w-3.5" />
                                )}
                                <span className="ml-1">Pause</span>
                              </Button>
                            ) : enrollment.status === "paused" ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  resumeMutation.mutate(enrollment.id);
                                }}
                                disabled={resumeMutation.isPending}
                                data-testid={`button-resume-${enrollment.id}`}
                              >
                                {resumeMutation.isPending ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Play className="h-3.5 w-3.5" />
                                )}
                                <span className="ml-1">Resume</span>
                              </Button>
                            ) : null}
                          </div>
                        )}
                      </div>

                      {/* Expanded jobs panel */}
                      {selectedEnrollmentId === enrollment.id && (
                        <div
                          className="mt-4 pt-4 border-t border-gray-100"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                            Upcoming / Recent Jobs
                          </p>
                          {enrollmentJobsQuery.isLoading ? (
                            <Skeleton className="h-16 rounded" />
                          ) : !enrollmentJobsQuery.data?.length ? (
                            <p className="text-sm text-gray-400">No jobs synced yet.</p>
                          ) : (
                            <div className="space-y-1">
                              {enrollmentJobsQuery.data.slice(0, 10).map((job) => (
                                <div
                                  key={job.id}
                                  className="flex items-center gap-3 text-sm py-1"
                                  data-testid={`job-row-${job.id}`}
                                >
                                  <StatusBadge status={job.status} />
                                  <span className="text-gray-700">
                                    {formatDate(job.scheduledDate)}
                                  </span>
                                  <span className="text-gray-400 capitalize">{job.type}</span>
                                  {job.completedAt && (
                                    <span className="text-green-600 text-xs">
                                      Done {formatDate(job.completedAt)}
                                    </span>
                                  )}
                                  <span className="ml-auto">
                                    {job.evidencePhotoUrl ? (
                                      <EvidenceThumbnail
                                        url={job.evidencePhotoUrl}
                                        label={`${getPropertyLabel(enrollment.propertyId)} — ${formatDate(job.scheduledDate)}`}
                                        onOpen={setLightboxPhoto}
                                        testId={`thumb-job-evidence-${job.id}`}
                                      />
                                    ) : job.status === "completed" ? (
                                      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                                        <Camera className="h-3.5 w-3.5" />
                                        No photo
                                      </span>
                                    ) : null}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Jobs */}
          <TabsContent value="jobs">
            <div className="flex items-center justify-between mb-3 gap-3">
              <div className="w-64 max-w-full">
                <SearchableSelect
                  value={jobsPropertyFilter}
                  onValueChange={setJobsPropertyFilter}
                  placeholder="All properties"
                  searchPlaceholder="Search properties..."
                  emptyText="No properties match."
                  testId="select-jobs-property-filter"
                  options={[
                    { value: "all", label: "All properties" },
                    ...enrolledPropertyIds.map((id) => {
                      const p = propertyMap.get(id);
                      return {
                        value: String(id),
                        label: getPropertyLabel(id),
                        keywords: `${p?.name ?? ""} ${p?.address ?? ""}`,
                      };
                    }),
                  ]}
                />
              </div>
              {jobsPropertyFilter !== "all" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setJobsPropertyFilter("all")}
                  data-testid="button-clear-jobs-filter"
                >
                  Clear filter
                </Button>
              )}
            </div>
            {jobsQuery.isLoading ? (
              <Skeleton className="h-48 rounded-lg" />
            ) : !filteredJobs.length ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <Truck className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No jobs found</p>
                  <p className="text-sm mt-1">
                    {jobsPropertyFilter !== "all"
                      ? "No jobs for the selected property."
                      : "Jobs appear here once synced from the collection service."}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Property</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Completed</TableHead>
                        <TableHead>Evidence</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredJobs.map((job) => {
                        const jobPropertyId =
                          job.enrollmentId != null
                            ? enrollmentPropertyMap.get(job.enrollmentId) ?? null
                            : null;
                        return (
                        <TableRow key={job.id} data-testid={`job-row-${job.id}`}>
                          <TableCell>{getPropertyLabel(jobPropertyId)}</TableCell>
                          <TableCell>{formatDate(job.scheduledDate)}</TableCell>
                          <TableCell className="capitalize">{job.type}</TableCell>
                          <TableCell>
                            <StatusBadge status={job.status} />
                          </TableCell>
                          <TableCell>{formatDate(job.completedAt)}</TableCell>
                          <TableCell data-testid={`cell-job-evidence-${job.id}`}>
                            {job.evidencePhotoUrl ? (
                              <div className="flex items-center gap-1.5">
                                <EvidenceThumbnail
                                  url={job.evidencePhotoUrl}
                                  label={`${getPropertyLabel(jobPropertyId)} — ${formatDate(job.scheduledDate)}`}
                                  onOpen={setLightboxPhoto}
                                  testId={`thumb-job-evidence-${job.id}`}
                                />
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                  Confirmed
                                </span>
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                                <Camera className="h-3.5 w-3.5" />
                                Pending
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-gray-500 text-sm">{job.notes ?? "—"}</TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* On-Demand Requests */}
          <TabsContent value="requests">
            {requestsQuery.isLoading ? (
              <Skeleton className="h-48 rounded-lg" />
            ) : !requestsQuery.data?.length ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <Send className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No on-demand requests yet</p>
                  <p className="text-sm mt-1">Use the On-Demand button to request an extra pickup.</p>
                </CardContent>
              </Card>
            ) : (
              <OnDemandRequestsTable
                requests={requestsQuery.data}
                getPropertyLabel={getPropertyLabel}
              />
            )}
          </TabsContent>

          {/* Coverage Map */}
          <TabsContent value="coverage">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-1.5">
                  <Truck className="h-4 w-4 text-brand" />
                  Service Area Coverage
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Coverage zones are shaded in blue. Properties inside a zone show a green
                  marker; those outside show grey and aren't eligible for scheduled or
                  on-demand service.
                </p>
              </CardHeader>
              <CardContent>
                <TrashCoverageMap
                  properties={sortedProperties}
                  enrollmentByPropertyId={enrollmentByPropertyId}
                  onEnroll={!isViewOnly ? handleMapEnroll : undefined}
                  onRequestPickup={!isViewOnly ? handleMapRequestPickup : undefined}
                  onPause={!isViewOnly ? handleMapPause : undefined}
                  onResume={!isViewOnly ? handleMapResume : undefined}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing */}
          <TabsContent value="billing">
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-1.5">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  Per-property billing status
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {enrollmentsQuery.isLoading ? (
                  <div className="p-6"><Skeleton className="h-24 w-full" /></div>
                ) : !enrollmentsQuery.data?.length ? (
                  <div className="py-10 text-center text-gray-500 text-sm" data-testid="text-no-billing-enrollments">
                    No enrolled properties yet.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Property</TableHead>
                        <TableHead>Monthly Fee</TableHead>
                        <TableHead>Billing Status</TableHead>
                        <TableHead>Next Billing</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enrollmentsQuery.data.map((e) => (
                        <TableRow key={e.id} data-testid={`billing-row-${e.id}`}>
                          <TableCell>{getPropertyLabel(e.propertyId)}</TableCell>
                          <TableCell data-testid={`text-billing-fee-${e.id}`}>
                            {e.monthlyServiceFeeCents != null
                              ? `${formatCents(e.monthlyServiceFeeCents)}/mo`
                              : `${formatCents(trashMonthlyFeeCents(e.recycleEnabled))}/mo`}
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              status={e.billingStatus ?? "pending"}
                              label={
                                e.billingStatus === "active"
                                  ? "Active"
                                  : e.billingStatus === "paused"
                                  ? "Paused"
                                  : "Pending approval"
                              }
                            />
                          </TableCell>
                          <TableCell data-testid={`text-next-billing-${e.id}`}>
                            {e.nextBillingDate ? formatDate(e.nextBillingDate) : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Charge history</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {chargesQuery.isLoading ? (
                  <div className="p-6"><Skeleton className="h-24 w-full" /></div>
                ) : !chargesQuery.data?.length ? (
                  <div className="py-10 text-center text-gray-500 text-sm" data-testid="text-no-charges">
                    No charges yet.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Property</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {chargesQuery.data.map((c) => (
                        <TableRow key={c.id} data-testid={`charge-row-${c.id}`}>
                          <TableCell>{formatDate(c.createdAt)}</TableCell>
                          <TableCell>{getPropertyLabel(c.propertyId)}</TableCell>
                          <TableCell className="capitalize">{c.type.replace(/_/g, " ")}</TableCell>
                          <TableCell>{c.periodMonth ?? "—"}</TableCell>
                          <TableCell data-testid={`text-charge-amount-${c.id}`}>{formatCents(c.amountCents)}</TableCell>
                          <TableCell>
                            <StatusBadge
                              status={
                                c.status === "succeeded"
                                  ? "active"
                                  : c.status === "failed"
                                  ? "failed"
                                  : "pending"
                              }
                              label={
                                c.status === "succeeded"
                                  ? "Succeeded"
                                  : c.status === "failed"
                                  ? "Failed"
                                  : "Pending"
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Enroll Dialog */}
        <EnrollmentDialog
          open={showEnrollDialog}
          onOpenChange={setShowEnrollDialog}
          form={enrollForm}
          setForm={setEnrollForm}
          enrollableProperties={enrollableProperties}
          vendorCards={vendorCards}
          assignedCardIdFor={assignedCardIdFor}
          onSubmit={() => createEnrollmentMutation.mutate(enrollForm)}
          isPending={createEnrollmentMutation.isPending}
        />

        {/* On-Demand Dialog */}
        <OnDemandRequestDialog
          open={showOnDemandDialog}
          onOpenChange={setShowOnDemandDialog}
          form={onDemandForm}
          setForm={setOnDemandForm}
          eligibleProperties={onDemandEligibleProperties}
          serviceabilityFetching={serviceabilityQuery.isFetching}
          serviceabilityData={serviceabilityQuery.data as ServiceabilityResult | undefined}
          isPropertyEligible={isOnDemandPropertyEligible}
          vendorCards={vendorCards}
          photoUploading={photoUploading}
          isPending={onDemandMutation.isPending}
          assignedCardIdFor={assignedCardIdFor}
          onCancel={() => setShowOnDemandDialog(false)}
          onSubmit={() => onDemandMutation.mutate(onDemandForm)}
          onPhotoChange={(e) =>
            handleOnDemandPhotoUpload({
              file: e.target.files?.[0],
              request: apiRequest,
              toast,
              setPhotoUploading,
              setForm: setOnDemandForm,
            })
          }
        />

        {/* Evidence photo lightbox */}
        <Dialog open={lightboxPhoto !== null} onOpenChange={(open) => !open && setLightboxPhoto(null)}>
          <DialogContent className="max-w-3xl" data-testid="dialog-evidence-lightbox">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <ImageIcon className="h-4 w-4 text-brand" />
                Collection-day evidence
              </DialogTitle>
            </DialogHeader>
            {lightboxPhoto && (
              <div className="space-y-2">
                <p className="text-sm text-gray-500" data-testid="text-evidence-label">
                  {lightboxPhoto.label}
                </p>
                <a
                  href={lightboxPhoto.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  data-testid="link-evidence-fullsize"
                >
                  <img
                    src={lightboxPhoto.url}
                    alt={`Collection evidence — ${lightboxPhoto.label}`}
                    className="max-h-[70vh] w-full rounded-lg object-contain bg-gray-50"
                    data-testid="img-evidence-fullsize"
                  />
                </a>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
